// The invoice report's vocabulary: the criteria a user sets, the rows the server
// hands back, the two caps, and the one function that turns criteria into the
// sentences the cover page prints.
//
// HERE rather than in `dto.ts` because `describeCriteria` is behaviour, not a
// type, and both sides of the wire need it: the server stores the normalized
// criteria on the audit row, and the browser prints them on the cover. One
// implementation means the record and the page cannot describe the same report
// two different ways.

import type { DocumentType, InvoiceStatus } from './enums.js'
import { formatCurrencyUSD, parseMoney, type MoneyString } from './money.js'

/**
 * The most invoices one report may cover. Enforced SERVER-SIDE, before the audit
 * row is written and before any presigned URL is issued — a report is refused
 * whole rather than truncated, because a listing that silently stops at 250 of
 * 400 is worse than no listing.
 */
export const REPORT_INVOICE_CAP = 250

/**
 * The most bytes one assembled report may reach. Enforced in the BROWSER, as it
 * assembles: the server cannot know a packet's size without opening it, and the
 * cap exists to stop a tab from exhausting its own memory.
 */
export const REPORT_MAX_BYTES = 200 * 1024 * 1024

/** The orders a report may be listed and assembled in. The first is the default. */
export const REPORT_SORTS = [
  'invoice_date_asc',
  'invoice_date_desc',
  'company_asc',
  'vendor_asc',
  'total_desc',
] as const
export type ReportSort = (typeof REPORT_SORTS)[number]

const DEFAULT_REPORT_SORT: ReportSort = 'invoice_date_asc'

/**
 * What a user set on the Reporting form. Every field optional; an empty object
 * means every invoice the caller can see.
 *
 * MONEY IS A DECIMAL STRING here, not a JSON number — the global convention for
 * payloads (`docs/api.md`). `GET /invoices` takes `total_min` / `total_max` as
 * query-string numbers and predates the rule; this body follows it. The repository
 * layer binds either as text and casts `::numeric` in SQL, so the wire type is a
 * contract question rather than a behavioural one (`docs/decisions.md`).
 *
 * The three general-ledger fields are LINE-ITEM level: an invoice matches when one
 * of its active line items carries the code. Supplying two of them means ONE line
 * carrying both, not two lines carrying one each.
 */
export interface ReportCriteria {
  company_id?: string[]
  vendor_id?: string[]
  status?: InvoiceStatus[]
  /** `invoices.invoice_date`, the date the vendor printed. */
  date_from?: string
  date_to?: string
  /** `invoices.uploaded_at`, the date it reached us. */
  uploaded_from?: string
  uploaded_to?: string
  /** When Export was clicked at Export Control (D-440). */
  exported_from?: string
  exported_to?: string
  total_min?: MoneyString
  total_max?: MoneyString
  document_type?: DocumentType[]
  currency?: string[]
  uploader_email?: string
  upload_id?: string
  export_id?: string
  manual_entry?: boolean
  has_active_hold?: boolean
  unresolved_flags?: boolean
  /** Flag types among the invoice's UNRESOLVED flags. */
  flag_type?: string[]
  /** Flag types among ALL the invoice's flags, resolved or not — the pair to
      `flag_type`, mirroring `total_flag_count` beside `unresolved_flag_count`
      on the header view. A resolved flag is history, and history is exactly
      what a report is sometimes for. */
  flag_type_any?: string[]
  /** The composed general-ledger code, `NNNNNN-NN-NNN`. */
  code_id?: string[]
  /** The code's leading segment — the department charged. */
  department_id?: string[]
  /** The code's trailing segment — what was bought. */
  account_id?: string[]
  q?: string
  sort?: ReportSort
}

/** One general-ledger code on an invoice, with the amount coded to it. */
export interface ReportCodeSubtotal {
  /** The composed code, `NNNNNN-NN-NNN`. */
  code: string
  /** Money as a decimal string, or null where no line under it carries one. */
  subtotal: MoneyString | null
}

/**
 * One invoice in a report, as the server hands it back: the summary values the
 * cover page prints, plus where its pages live.
 *
 * `upload_page_start` / `upload_page_end` are the span inside the upload packet
 * (`docs/schema.md`). Both null — a manual-entry invoice, or a row from before
 * splitting — means the invoice is LISTED with no source pages, never dropped.
 */
export interface ReportInvoiceRow {
  id: string
  invoice_date: string | null
  vendor_name: string | null
  company_name: string | null
  external_invoice_id: string | null
  total: MoneyString | null
  status: InvoiceStatus
  upload_id: string | null
  upload_page_start: number | null
  upload_page_end: number | null
  /**
   * The invoice's general-ledger codes with the amount coded to each, one entry
   * per distinct code, summed over the active line items and ordered by code.
   *
   * A LIST OF PAIRS, not a list of codes: the cover page subtotals per code
   * (owner, 2026-09-10), so the amount has to travel with the code. `subtotal`
   * is null where every line under that code has no amount yet.
   */
  code_subtotals: ReportCodeSubtotal[]
}

/**
 * The criteria as the server will store and run them: trimmed, de-duplicated,
 * with every empty array, blank string and absent key REMOVED.
 *
 * Why normalize at all, rather than storing the body as sent: the audit row and
 * the cover page both describe the criteria, and a form that submits
 * `{ company_id: [], q: '   ' }` for "I set nothing" would otherwise be recorded
 * as though two criteria had been set. Normalizing once, server-side, means the
 * record, the page and the query all describe the same report.
 *
 * `sort` is normalized too — an absent or unrecognized value becomes the
 * default rather than an error, because the order of a listing is not worth
 * refusing a report over. Every OTHER unrecognized value is a validation
 * failure, decided by the route; this function assumes a well-typed object and
 * only tidies it.
 */
export function normalizeCriteria(criteria: ReportCriteria): ReportCriteria {
  const out: ReportCriteria = {}

  const list = <T extends string>(values: readonly T[] | undefined): T[] | undefined => {
    if (values === undefined) return undefined
    const kept = [...new Set(values.map((v) => v.trim()).filter((v) => v !== ''))]
    return kept.length > 0 ? (kept as T[]) : undefined
  }
  const one = (value: string | undefined): string | undefined => {
    const trimmed = (value ?? '').trim()
    return trimmed === '' ? undefined : trimmed
  }

  const companies = list(criteria.company_id)
  if (companies !== undefined) out.company_id = companies
  const vendors = list(criteria.vendor_id)
  if (vendors !== undefined) out.vendor_id = vendors
  const statuses = list(criteria.status)
  if (statuses !== undefined) out.status = statuses

  const dateFrom = one(criteria.date_from)
  if (dateFrom !== undefined) out.date_from = dateFrom
  const dateTo = one(criteria.date_to)
  if (dateTo !== undefined) out.date_to = dateTo
  const uploadedFrom = one(criteria.uploaded_from)
  if (uploadedFrom !== undefined) out.uploaded_from = uploadedFrom
  const uploadedTo = one(criteria.uploaded_to)
  if (uploadedTo !== undefined) out.uploaded_to = uploadedTo
  const exportedFrom = one(criteria.exported_from)
  if (exportedFrom !== undefined) out.exported_from = exportedFrom
  const exportedTo = one(criteria.exported_to)
  if (exportedTo !== undefined) out.exported_to = exportedTo

  const totalMin = one(criteria.total_min)
  if (totalMin !== undefined) out.total_min = totalMin
  const totalMax = one(criteria.total_max)
  if (totalMax !== undefined) out.total_max = totalMax

  const codes = list(criteria.code_id)
  if (codes !== undefined) out.code_id = codes
  const departments = list(criteria.department_id)
  if (departments !== undefined) out.department_id = departments
  const accounts = list(criteria.account_id)
  if (accounts !== undefined) out.account_id = accounts

  // A boolean is meaningful in BOTH states — `false` is "exclude these", not
  // "unset" — so only `undefined` is dropped.
  if (criteria.manual_entry !== undefined) out.manual_entry = criteria.manual_entry
  if (criteria.has_active_hold !== undefined) out.has_active_hold = criteria.has_active_hold
  if (criteria.unresolved_flags !== undefined) out.unresolved_flags = criteria.unresolved_flags

  const flags = list(criteria.flag_type)
  if (flags !== undefined) out.flag_type = flags
  const anyFlags = list(criteria.flag_type_any)
  if (anyFlags !== undefined) out.flag_type_any = anyFlags
  const documentTypes = list(criteria.document_type)
  if (documentTypes !== undefined) out.document_type = documentTypes
  const currencies = list(criteria.currency)
  if (currencies !== undefined) out.currency = currencies

  const uploader = one(criteria.uploader_email)
  if (uploader !== undefined) out.uploader_email = uploader
  const upload = one(criteria.upload_id)
  if (upload !== undefined) out.upload_id = upload
  const exportBatch = one(criteria.export_id)
  if (exportBatch !== undefined) out.export_id = exportBatch
  const query = one(criteria.q)
  if (query !== undefined) out.q = query

  out.sort = isReportSort(criteria.sort) ? criteria.sort : DEFAULT_REPORT_SORT
  return out
}

/** Is this one of the five orders a report may take? */
function isReportSort(value: unknown): value is ReportSort {
  return typeof value === 'string' && (REPORT_SORTS as readonly string[]).includes(value)
}

/**
 * Display names for the ids a criteria object holds, so `describeCriteria` never
 * guesses one. The caller supplies what it has; a missing entry falls back to the
 * id itself, which is honest rather than blank.
 */
export interface CriteriaLabels {
  companies?: Record<string, string>
  vendors?: Record<string, string>
  flags?: Record<string, string>
}

function nonEmpty(values: readonly string[] | undefined): string[] {
  if (values === undefined) return []
  return values.map((v) => v.trim()).filter((v) => v !== '')
}

function text(value: string | undefined): string | null {
  const trimmed = (value ?? '').trim()
  return trimmed === '' ? null : trimmed
}

function named(values: readonly string[], labels: Record<string, string> | undefined): string {
  return values.map((v) => labels?.[v] ?? v).join(', ')
}

/** `from X`, `up to Y`, or `X to Y`. One of the two bounds is always present. */
function range(from: string | null, to: string | null): string {
  if (from !== null && to !== null) return `${from} to ${to}`
  return from !== null ? `from ${from}` : `up to ${to}`
}

function money(value: MoneyString | undefined): string | null {
  const trimmed = text(value)
  if (trimmed === null) return null
  const cents = parseMoney(trimmed)
  return cents === null ? null : formatCurrencyUSD(cents)
}

/**
 * The criteria as sentences, in a FIXED order that does not depend on the object's
 * key order — the cover page and the audit row must read the same way for the same
 * report, whichever built the object.
 *
 * An empty criteria object is one line saying so, because a cover page that lists
 * nothing under "Criteria" reads like a bug.
 */
export function describeCriteria(criteria: ReportCriteria, labels: CriteriaLabels = {}): string[] {
  const lines: string[] = []

  const companies = nonEmpty(criteria.company_id)
  if (companies.length > 0) lines.push(`Companies: ${named(companies, labels.companies)}`)

  const vendors = nonEmpty(criteria.vendor_id)
  if (vendors.length > 0) lines.push(`Vendors: ${named(vendors, labels.vendors)}`)

  const statuses = nonEmpty(criteria.status)
  if (statuses.length > 0) lines.push(`Status: ${statuses.join(', ')}`)

  const dateFrom = text(criteria.date_from)
  const dateTo = text(criteria.date_to)
  if (dateFrom !== null || dateTo !== null) {
    lines.push(`Invoice Date: ${range(dateFrom, dateTo)}`)
  }

  const uploadedFrom = text(criteria.uploaded_from)
  const uploadedTo = text(criteria.uploaded_to)
  if (uploadedFrom !== null || uploadedTo !== null) {
    lines.push(`Uploaded Date: ${range(uploadedFrom, uploadedTo)}`)
  }

  const exportedFrom = text(criteria.exported_from)
  const exportedTo = text(criteria.exported_to)
  if (exportedFrom !== null || exportedTo !== null) {
    lines.push(`Exported Date: ${range(exportedFrom, exportedTo)}`)
  }

  const totalMin = money(criteria.total_min)
  const totalMax = money(criteria.total_max)
  if (totalMin !== null || totalMax !== null) {
    lines.push(`Total Range: ${range(totalMin, totalMax)}`)
  }

  // `code_id` is the COMPOSED code: both halves together.
  const codes = nonEmpty(criteria.code_id)
  if (codes.length > 0) lines.push(`Codes: ${codes.join(', ')}`)

  const departments = nonEmpty(criteria.department_id)
  if (departments.length > 0) lines.push(`Departments: ${departments.join(', ')}`)

  // `account_id` is the trailing half, printed as Account (D-85).
  const accounts = nonEmpty(criteria.account_id)
  if (accounts.length > 0) lines.push(`Accounts: ${accounts.join(', ')}`)

  if (criteria.manual_entry === true) lines.push('Manual entry only')
  if (criteria.manual_entry === false) lines.push('Excluding manual entry')

  if (criteria.has_active_hold === true) lines.push('On Hold: Held')
  if (criteria.has_active_hold === false) lines.push('On Hold: Not Held')

  if (criteria.unresolved_flags === true) lines.push('With unresolved flags only')
  if (criteria.unresolved_flags === false) lines.push('With no unresolved flags')

  const anyFlags = nonEmpty(criteria.flag_type_any)
  if (anyFlags.length > 0) lines.push(`Total Flags: ${named(anyFlags, labels.flags)}`)

  const flags = nonEmpty(criteria.flag_type)
  if (flags.length > 0) lines.push(`Unresolved Flags: ${named(flags, labels.flags)}`)

  const documentTypes = nonEmpty(criteria.document_type)
  if (documentTypes.length > 0) lines.push(`Document types: ${documentTypes.join(', ')}`)

  const currencies = nonEmpty(criteria.currency)
  if (currencies.length > 0) lines.push(`Currencies: ${currencies.join(', ')}`)

  const uploader = text(criteria.uploader_email)
  if (uploader !== null) lines.push(`Uploaded by ${uploader}`)

  const upload = text(criteria.upload_id)
  if (upload !== null) lines.push(`Upload ${upload}`)

  const exportBatch = text(criteria.export_id)
  if (exportBatch !== null) lines.push(`Export batch ${exportBatch}`)

  const query = text(criteria.q)
  if (query !== null) lines.push(`Text search "${query}"`)

  // `sort` is deliberately absent: it decides the ORDER of the listing, not which
  // invoices are in it, so printing it under "Criteria" would misdescribe it.
  return lines.length > 0 ? lines : ['All invoices you can see']
}
